<template>
  <div ref="reportContent" class="report-clipboard" v-html="formattedReport" style="display: none;"></div>
</template>

<script>
export default {
  name: "ReportClipboard",
  props: {
    reportData: {
      type: Object,
      required: true,
    },
  },
  computed: {
    formattedReport() {
      const {
        shift = 'Shift',
        date = 'Unknown Date',
        monitor = 'Unknown Monitor',
        headerColor = '#000000',
        attendance = [],
        salesData = [],
        modelFocus = [],
        breakItems = [],
        adjustmentList = [],
        shiftObservations = [],
        checklist = [],
        platformTotals = {},
        grandTotal = 0
      } = this.reportData || {};

      const capitalizeShift = shift.charAt(0).toUpperCase() + shift.slice(1);
      const headerStyle = `background-color: ${headerColor}; color: #fff; padding: 8px; font-size: 18px; font-weight: bold; text-align: center;`;
      const subHeaderStyle = `background-color: ${headerColor}; color: #fff; padding: 6px; font-size: 14px; font-weight: bold; text-align: left;`;
      const tableStyle = `width: 100%; border-collapse: collapse; margin-top: 8px; font-size: 12px;`;
      const thStyle = `border: 1px solid #ddd; padding: 6px; background-color: #f5f5f5; text-align: left;`;
      const tdStyle = `border: 1px solid #ddd; padding: 6px; text-align: left;`;

      return `
        <div style="font-family: Arial, sans-serif; color: #333; max-width: 800px; margin: 0 auto;">
          <div style="overflow-x: auto;">
            <div style="width: 800px; margin: 0 auto;">
              <div style="${headerStyle}">
                ${capitalizeShift} Report&nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;${monitor}&nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;Studio CC&nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;${date}
              </div>
            </div>
          </div>

          <div style="margin-top: 12px;">
            <div style="overflow-x: auto;">
              <div style="width: 800px; margin: 0 auto;">
                <div style="${subHeaderStyle}; text-align: left;">Assistance</div>
                <table style="${tableStyle}">
                  <thead>
                    <tr>
                      <th style="${thStyle}">Model Name</th>
                      <th style="${thStyle}">Stage Name</th>
                      <th style="${thStyle}">Room #</th>
                      <th style="${thStyle}">Assistance</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${attendance.length > 0 ? attendance.map(item => `
                      <tr>
                        <td style="${tdStyle} width: 240px;">${item.fullName || '-'}</td>
                        <td style="${tdStyle} width: 160px;">${item.stageName || '-'}</td>
                        <td style="${tdStyle}">${item.room || '-'}</td>
                        <td style="${tdStyle}">${item.assistance || '-'}</td>
                      </tr>
                    `).join('') : `<tr><td style="${tdStyle}" colspan="4">No attendance data</td></tr>`}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div style="margin-top: 12px;">
            <div style="overflow-x: auto;">
              <div style="width: 800px; margin: 0 auto;">
                <div style="${subHeaderStyle}; text-align: left;">Time Tracking</div>
                <table style="${tableStyle}">
                  <thead>
                    <tr>
                      <th style="${thStyle}">Model Name</th>
                      <th style="${thStyle}">Arrival</th>
                      <th style="${thStyle}">Connection</th>
                      <th style="${thStyle}">Log-Out</th>
                      <th style="${thStyle}">Hours</th>
                    </tr>
                  </thead>
                  <tbody>
                  ${
                    salesData
                      .filter(item => item.arrival !== '-' || item.connection !== '-' || item.logout !== '-' || item.hours !== '-') // Only include rows with at least one non-empty time field
                      .map(item => `
                        <tr>
                          <td style="${tdStyle} width: 240px;">${item.fullName || '-'}</td>
                          <td style="${tdStyle}">${item.arrival || '-'}</td>
                          <td style="${tdStyle}">${item.connection || '-'}</td>
                          <td style="${tdStyle}">${item.logout || '-'}</td>
                          <td style="${tdStyle}">${item.hours || '-'}</td>
                        </tr>
                      `).join('') || `<tr><td style="${tdStyle}" colspan="5">No time data</td></tr>`
                  }
                </tbody>
                </table>
              </div>
            </div>
          </div>

          <div style="margin-top: 12px;">
            <div style="overflow-x: auto;">
              <div style="width: 800px; margin: 0 auto;">
                <div style="${subHeaderStyle}; text-align: left;">Sales</div>
                <table style="${tableStyle}">
                  <thead>
                    <tr>
                      <th style="${thStyle}">Model Name</th>
                      <th style="${thStyle}">Flirt4Free</th>
                      <th style="${thStyle}">Streamate</th>
                      <th style="${thStyle}">LiveJasmin</th>
                      <th style="${thStyle}">Chaturbate</th>
                      <th style="${thStyle}">StripChat</th>
                      <th style="${thStyle}">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${salesData.length > 0 ? salesData.map(sale => `
                      <tr>
                        <td style="${tdStyle} width: 240px;">${sale.fullName || '-'}</td>
                        <td style="${tdStyle}">$${parseFloat(sale.flirt4free || 0).toFixed(2)}</td>
                        <td style="${tdStyle}">$${parseFloat(sale.streamate || 0).toFixed(2)}</td>
                        <td style="${tdStyle}">$${parseFloat(sale.livejasmin || 0).toFixed(2)}</td>
                        <td style="${tdStyle}">$${parseFloat(sale.chaturbate || 0).toFixed(2)}</td>
                        <td style="${tdStyle}">$${parseFloat(sale.stripchat || 0).toFixed(2)}</td>
                        <td style="${tdStyle}; background-color: ${
                          sale.goalColor === 'green-bg' ? '#d4edda' :
                          sale.goalColor === 'yellow-bg' ? '#fff3cd' :
                          sale.goalColor === 'red-bg' ? '#f8d7da' : 'transparent'
                        };">
                          $${parseFloat(sale.total || 0).toFixed(2)}
                        </td>
                      </tr>
                    `).join('') : `<tr><td style="${tdStyle}" colspan="7">No sales data</td></tr>`}
                    <tr>
                      <td style="${tdStyle} font-weight: bold;">Total</td>
                      <td style="${tdStyle}">$${parseFloat(platformTotals.Flirt4Free || 0).toFixed(2)}</td>
                      <td style="${tdStyle}">$${parseFloat(platformTotals.Streamate || 0).toFixed(2)}</td>
                      <td style="${tdStyle}">$${parseFloat(platformTotals.LiveJasmin || 0).toFixed(2)}</td>
                      <td style="${tdStyle}">$${parseFloat(platformTotals.Chaturbate || 0).toFixed(2)}</td>
                      <td style="${tdStyle}">$${parseFloat(platformTotals.StripChat || 0).toFixed(2)}</td>
                      <td style="${tdStyle}; font-weight: bold; background-color: ${
                        grandTotal >= 300 ? '#d4edda' :
                        grandTotal >= 100 ? '#fff3cd' :
                        '#f8d7da'
                      };">
                        $${parseFloat(grandTotal || 0).toFixed(2)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div style="margin-top: 12px;">
            <div style="overflow-x: auto;">
              <div style="width: 800px; margin: 0 auto;">
                <div style="${subHeaderStyle}; text-align: left;">Model Focus</div>
                <table style="${tableStyle}">
                  <thead>
                    <tr>
                      <th style="${thStyle}">Model Name</th>
                      <th style="${thStyle}">Actions & Observations</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${modelFocus.length > 0 ? modelFocus.map(item => `
                      <tr>
                       <td style="${tdStyle} width: 240px;">
                          ${item.focus || '-'}<br>
                          <span style="font-size: 0.75rem; color: #888;">
                            ${item.stageName || '-'}&nbsp;&nbsp;&nbsp;-&nbsp;&nbsp;&nbsp;Room ${item.room || '-'}
                          </span>
                        </td>
                        <td style="${tdStyle}">${item.notes || '-'}</td>
                      </tr>
                    `).join('') : `<tr><td style="${tdStyle}" colspan="2">No model focus data</td></tr>`}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div style="margin-top: 12px;">
            <div style="overflow-x: auto;">
              <div style="width: 800px; margin: 0 auto;">
                <div style="${subHeaderStyle}; text-align: left;">Break & Usage Tracking</div>
                <table style="${tableStyle}">
                  <thead>
                    <tr>
                      <th style="${thStyle}">Break</th>
                      <th style="${thStyle}">Lush 1</th>
                      <th style="${thStyle}">Lush 2</th>
                      <th style="${thStyle}">Sex Machine</th>
                      <th style="${thStyle}">Cellphone</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${breakItems.length > 0 ? breakItems.map(entry => `
                      <tr>
                        <td style="${tdStyle}">${entry.break || '-'}</td>
                        <td style="${tdStyle}">${entry.lush1 || '-'}</td>
                        <td style="${tdStyle}">${entry.lush2 || '-'}</td>
                        <td style="${tdStyle}">${entry.machine || '-'}</td>
                        <td style="${tdStyle}">${entry.cellphone || '-'}</td>
                      </tr>
                    `).join('') : `<tr><td style="${tdStyle}" colspan="5">No break/usage data</td></tr>`}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div style="margin-top: 12px;">
            <div style="overflow-x: auto;">
              <div style="width: 800px; margin: 0 auto;">
                <div style="${subHeaderStyle}; text-align: left;">Adjustments & Purchases</div>
                <table style="${tableStyle}">
                  <thead>
                    <tr>
                      <th style="${thStyle}">Name</th>
                      <th style="${thStyle}">Type</th>
                      <th style="${thStyle}">Amount</th>
                      <th style="${thStyle}">Description</th>
                      <th style="${thStyle}">Quotes</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${adjustmentList.length > 0 ? adjustmentList.map(adj => `
                      <tr>
                        <td style="${tdStyle} width: 240px;">${adj.name || '-'}</td>
                        <td style="${tdStyle} width: 100px;">${adj.type || '-'}</td>
                        <td style="${tdStyle} width: 100px;">${adj.currency === 'USD' ? '$' : 'COP '}${parseFloat(adj.amount || 0).toFixed(2)}</td>
                        <td style="${tdStyle}">${adj.description || '-'}</td>
                        <td style="${tdStyle} width: 50px;">${adj.quotas || '-'}</td>
                      </tr>
                    `).join('') : `<tr><td style="${tdStyle}" colspan="5">No adjustments</td></tr>`}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div style="margin-top: 12px;">
            <div style="overflow-x: auto;">
              <div style="width: 800px; margin: 0 auto;">
                <div style="${subHeaderStyle}; text-align: left;">Shift Observations</div>
                <table style="${tableStyle}">
                  <tbody>
                    ${shiftObservations.length > 0 ? shiftObservations.map(obs => `
                      <tr>
                        <td style="${tdStyle}">${obs || '-'}</td>
                      </tr>
                    `).join('') : `<tr><td style="${tdStyle}">No observations</td></tr>`}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div style="margin-top: 12px;">
            <div style="overflow-x: auto;">
              <div style="width: 800px; margin: 0 auto;">
                <div style="${subHeaderStyle}; text-align: left;">Checklist</div>
                <ul style="columns: 2; -webkit-columns: 2; -moz-columns: 2; list-style: none; padding-left: 0; margin: 8px 0;">
                  ${checklist.length > 0 ? checklist.map(item => `<li style="margin-bottom: 4px;">
                    <span style="font-size: 22px; display: inline-block; width: 24px;">${item.slice(0, 2)}</span>${item.slice(2)}
                  </li>`).join('') : '<li>No checklist items</li>'}
                </ul>
              </div>
            </div>
          </div>

          <div style="margin-top: 12px; font-size: 10px; color: #777; text-align: center;">
            Generated on ${new Date().toLocaleString()}
          </div>
        </div>
      `;
    },
  },
  methods: {
    getFormattedReportData() {
    const formatTime = (timeObj) => {
      if (!timeObj || !('hours' in timeObj) || !('minutes' in timeObj) || !('ampm' in timeObj)) return '-';
      return `${timeObj.hours}:${timeObj.minutes.toString().padStart(2, '0')} ${timeObj.ampm}`;
    };

    // Only include models whose assistance is marked as "Yes" for time tracking
    const allModelsWithTime = this.modelsList
      .filter(model => model.assistance && model.assistance.toLowerCase() === 'yes')
      .map(model => ({
        fullName: model.name,
        arrival: formatTime(model.arrivalTime),
        connection: formatTime(model.connectionTime),
        logout: formatTime(model.logoutTime),
        hours: this.calculateOnlineHours(model)
      }));

    // Sales data should include models with assistance "Yes" or "No (Offline Sales)"
    const salesData = this.earningsModels.map(model => {
      const timeData = allModelsWithTime.find(m => m.fullName === model.name) || {};
      return {
        fullName: model.name,
        flirt4free: model.earnings.Flirt4Free || 0,
        streamate: model.earnings.Streamate || 0,
        livejasmin: model.earnings.LiveJasmin || 0,
        chaturbate: model.earnings.Chaturbate || 0,
        stripchat: model.earnings.StripChat || 0,
        total: this.getRowTotal(model.earnings),
        arrival: timeData.arrival || '-',
        connection: timeData.connection || '-',
        logout: timeData.logout || '-',
        hours: timeData.hours || '-',
        goalColor: this.getEarningsClass(this.getRowTotal(model.earnings))
      };
    });

    return {
      shift: this.activeTab,
      date: this.formatDate(this.reportDate),
      monitor: this.selectedMonitor,
      headerColor: this.headerColor,
      attendance: this.modelsList.map(model => ({
        fullName: model.name,
        stageName: model.stageName || '-',
        room: model.room || '-',
        assistance: model.assistance || '-'
      })),
      salesData: salesData,
      modelFocus: this.modelFocusModels.map(model => ({
        focus: model.name,
        notes: model.observations || '-',
        stageName: model.stageName || '-',
        room: model.room || '-'
      })),
      breakItems: [{
        break: this.breakEntry || '-',
        lush1: this.usageEntries.lush1Entry || '-',
        lush2: this.usageEntries.lush2Entry || '-',
        machine: this.usageEntries.sexMachineEntry || '-',
        cellphone: this.usageEntries.cellphoneEntry || '-'
      }],
      adjustmentList: this.adjustmentList.map(adj => ({
        name: adj.name || '-',
        type: adj.type || '-',
        amount: adj.amount || 0,
        currency: adj.currency || 'COP',
        description: adj.description || '-',
        quotas: adj.quotes || '-'
      })),
      shiftObservations: this.observationsList.filter(obs => obs.trim() !== ''),
      checklist: this.checklistItems.map(item => `${item.checked ? '✅' : '❌'} ${item.text}`),
      platformTotals: this.platformTotals,
      grandTotal: this.grandTotal
    };
  },
    copyToClipboard() {
      const el = this.$refs.reportContent;
      if (!el) {
        console.error("Report content element not found");
        return;
      }

      el.style.display = 'block';
      const range = document.createRange();
      range.selectNodeContents(el);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);

      try {
        document.execCommand("copy");
        this.$emit("copied");
        console.log("Report copied to clipboard");
      } catch (err) {
        console.error("Failed to copy:", err);
        this.$emit("copy-failed", err);
      }

      selection.removeAllRanges();
      el.style.display = 'none';
    },
  },
};
</script>

<style scoped>
.report-clipboard {
  position: absolute;
  left: -9999px;
}
</style>